import type {
  ConstantKeyField,
  OfferGroupDataDto,
  PlanSelectionOption,
  PriceCategoryDataDto,
} from '@api/types.gen'

/**
 * Where a plan option gets its prices.
 *
 * Two sources, checked in this order:
 *  1. the component's price-category constant, at the rung the option names
 *     (`option.categoryKey`) — one constant re-prices every plan at once;
 *  2. the option's own offer-group constant (`option.offerGroup`) — how every
 *     option authored before price categories works, and still supported.
 *
 * Reads go through a `getData`-shaped callback rather than `usePipe` directly so
 * this stays testable and usable from any component in the render bundle.
 */
export type ReadPipeData = (path: string) => unknown

/**
 * The plan keys a price category can expose, in ladder order. Mirrors
 * PRICE_CATEGORY_KEYS in api/src/modules/price-categories/lib/priceCategoryKeys.ts
 * — widening one without the other leaves a key the editor offers and the
 * resolved value never carries (or the reverse).
 */
export const PRICE_CATEGORY_KEYS = ['s', 'm', 'l', 'xl'] as const

const isOfferGroup = (value: unknown): value is OfferGroupDataDto =>
  !!value && typeof value === 'object' && (value as OfferGroupDataDto).type === 'offer_group_value'

const isPriceCategory = (value: unknown): value is PriceCategoryDataDto =>
  !!value &&
  typeof value === 'object' &&
  (value as PriceCategoryDataDto).type === 'price_category_value'

/** The rungs a category actually populated, in ladder order. */
export const priceCategoryRungs = (
  category: PriceCategoryDataDto | undefined,
): { key: string; group: OfferGroupDataDto }[] => {
  if (!category) return []
  return PRICE_CATEGORY_KEYS.flatMap((key) => {
    const group = category[key]
    return isOfferGroup(group) ? [{ key, group }] : []
  })
}

/** The resolved price category a component's `priceCategory` field points at. */
export const readPriceCategory = (
  getData: ReadPipeData,
  field: ConstantKeyField | undefined,
): PriceCategoryDataDto | undefined => {
  if (!field?.key) return undefined
  const value = getData(`const.${field.key}`)
  return isPriceCategory(value) ? value : undefined
}

export const readOfferGroupForOption = (
  getData: ReadPipeData,
  option: PlanSelectionOption,
  category: PriceCategoryDataDto | undefined,
): OfferGroupDataDto | undefined => {
  if (option.categoryKey && category) {
    const group = category[option.categoryKey as keyof PriceCategoryDataDto]
    if (isOfferGroup(group)) return group
    // A rung the category does not populate — the author picked a slot the
    // current category is missing, or a swapped-in category is shorter. Fall
    // through so a legacy offerGroup still renders rather than blanking.
  }
  if (!option.offerGroup?.key) return undefined
  const value = getData(`const.${option.offerGroup.key}`)
  return isOfferGroup(value) ? value : undefined
}
